import { useQuery } from "@tanstack/react-query";
import { type Dayjs } from "dayjs";

import { type Order } from "@fitness-app/data-models/entities/Order";
import { type ArchivedInfo } from "@fitness-app/data-models/entities/ProductClient";

import { supabase } from "~/store/initializeStore";

export interface ArchivedProductClient {
  id: string;
  email: string;
  connectedOrders: string[] | null;
  archivedInfo: ArchivedInfo | null;
  /** The last charge of the plan; its cycle against the plan's installments says whether the plan ran to the end. */
  lastOrder: (Pick<Order, "id" | "createdAt" | "metadata" | "price"> & { paymentCycle: number | null }) | null;
}

/** PostgREST returns at most this many rows per request. */
const PAGE_SIZE = 1000;

const fetchArchivedClients = async (
  productId: string,
  archivedFrom: Dayjs | null,
): Promise<ArchivedProductClient[]> => {
  const clients: ArchivedProductClient[] = [];

  for (let page = 0; ; page += 1) {
    let query = supabase
      .from("product_client")
      .select("id,email,connectedOrders,archivedInfo,lastOrder:lastOrder(id,createdAt,metadata,price,paymentCycle)")
      .eq("productId", productId)
      .eq("status", "archived");

    if (archivedFrom) {
      // `archivedAt` is a unix timestamp stored as JSON; ten-digit values compare correctly as text.
      query = query.gte("archivedInfo->>archivedAt", String(archivedFrom.unix()));
    }

    const { data } = await query
      .order("id", { ascending: true })
      .range(page * PAGE_SIZE, (page + 1) * PAGE_SIZE - 1)
      .overrideTypes<ArchivedProductClient[]>()
      .throwOnError();

    clients.push(...data);

    if (data.length < PAGE_SIZE) {
      return clients;
    }
  }
};

/**
 * Clients of the product archived since the start of the range: a client who bought in the range
 * can only have resigned after it started, so this is every resignation the range's sales can carry.
 */
export const useArchivedProductClients = (productId: string, dateRange: [Dayjs, Dayjs] | null) => {
  const archivedFrom = dateRange?.[0]?.startOf("day") ?? null;

  return useQuery(["archivedProductClients", productId, archivedFrom?.format("YYYY-MM-DD") ?? null], {
    enabled: Boolean(productId),
    keepPreviousData: true,
    queryFn: () => fetchArchivedClients(productId, archivedFrom),
  });
};
